/**
 * メニューコントローラー
 * PC / SP 共通のグローバルナビゲーション（オーバーレイ）の開閉制御と
 * スクロール方向による gHeader / #fixedCV の表示／非表示（SPのみ）を担当する。
 */

import { _breakP } from './utils.js';

// ページ最上部とみなすスクロール位置（これ以下はヘッダーを常時表示）
const SCROLL_TOP_THRESHOLD = 60;
// ヘッダー表示/非表示の判定に必要なスクロール差分
const SCROLL_DELTA_THRESHOLD = 10;
// 開閉アニメーション終了後、最初のリンクにフォーカスを移すまでの待機時間（ms）
const FOCUS_DELAY = 300;

export default class MenuController {
  constructor() {
    // 対象要素
    this.header = document.getElementById('gHeader');
    this.fixedCV = document.getElementById('fixedCV');
    this.menuBtn = document.querySelector('.js-menuBtn');
    this.menu = document.getElementById('contentsMenu');
    this.overlay = this.menu ? this.menu.querySelector('.js-menuOverlay') : null;
    this.menuLinks = this.menu ? this.menu.querySelectorAll('.js-menuLink') : [];

    // 状態
    this.isOpen = false;
    this.isHeaderVisible = true;
    this.lastScrollY = 0;

    // 登録済みイベントリスナー（destroy でまとめて解除）
    this.listeners = [];

    // 必須要素が無ければ何もしない
    if (!this.menuBtn || !this.menu) return;

    this.init();
  }

  // ---------------------------------------------------------------
  // 初期化 / 破棄
  // ---------------------------------------------------------------

  init() {
    this.bindMenuEvents();
    this.bindHeaderScroll();
  }

  destroy() {
    this.listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this.listeners = [];
    this.isOpen = false;
    this.isHeaderVisible = true;
  }

  /**
   * destroy 時にまとめて解除できるよう、登録リスナーを保持するヘルパ
   */
  on(target, type, handler, options) {
    if (!target) return;
    target.addEventListener(type, handler, options);
    this.listeners.push({ target, type, handler, options });
  }

  // ---------------------------------------------------------------
  // メニュー開閉
  // ---------------------------------------------------------------

  bindMenuEvents() {
    // ハンバーガーボタン → 開閉トグル
    this.on(this.menuBtn, 'click', () => this.toggle());

    // オーバーレイクリック → 閉じる
    this.on(this.overlay, 'click', () => this.close());

    // メニュー内リンククリック → 閉じる
    this.menuLinks.forEach(link => {
      this.on(link, 'click', () => this.close());
    });

    // Esc キー → 開いていれば閉じる
    this.on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.menuBtn.classList.add('is-active');
    this.menuBtn.setAttribute('aria-expanded', 'true');
    this.menuBtn.setAttribute('aria-label', 'メニューを閉じる');
    this.menu.classList.add('is-visible');
    this.menu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-menu-open');

    // アニメーション完了後に最初のリンクへフォーカスを移す（a11y 対応）
    const firstLink = this.menu.querySelector('.js-menuLink');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), FOCUS_DELAY);
    }
  }

  close() {
    this.isOpen = false;
    this.menuBtn.classList.remove('is-active');
    this.menuBtn.setAttribute('aria-expanded', 'false');
    this.menuBtn.setAttribute('aria-label', 'メニューを開く');
    this.menu.classList.remove('is-visible');
    this.menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-menu-open');

    // 操作完了後、メニューボタンにフォーカスを戻す
    this.menuBtn.focus();
  }

  // ---------------------------------------------------------------
  // gHeader / #fixedCV のスクロール連動（SP のみ）
  // ---------------------------------------------------------------

  bindHeaderScroll() {
    if (!this.header && !this.fixedCV) return;

    // 初期表示
    this.setHeaderVisible(true);

    // rAF による間引き
    let ticking = false;
    this.on(window, 'scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        this.updateHeaderOnScroll();
        ticking = false;
      });
    }, { passive: true });
  }

  /**
   * 現在のスクロール位置・方向に応じて gHeader / #fixedCV の表示を更新する
   */
  updateHeaderOnScroll() {
    // PC では常時表示（関連クラスをリセット）
    if (!_breakP.SP) {
      this.header?.classList.remove('is-visible', 'is-hidden');
      this.fixedCV?.classList.remove('is-visible', 'is-hidden');
      return;
    }

    // メニュー展開中はヘッダーを固定（隠さない）
    if (this.isOpen) return;

    const currentY = window.scrollY;
    const delta = currentY - this.lastScrollY;

    if (currentY < SCROLL_TOP_THRESHOLD) {
      // ページ最上部は常に表示
      this.setHeaderVisible(true);
    } else if (delta > SCROLL_DELTA_THRESHOLD && this.isHeaderVisible) {
      // 下方向スクロール → 隠す
      this.setHeaderVisible(false);
    } else if (delta < -SCROLL_DELTA_THRESHOLD && !this.isHeaderVisible) {
      // 上方向スクロール → 表示
      this.setHeaderVisible(true);
    }

    this.lastScrollY = currentY;
  }

  setHeaderVisible(visible) {
    this.isHeaderVisible = visible;
    if (this.header) {
      this.header.classList.toggle('is-visible', visible);
      this.header.classList.toggle('is-hidden', !visible);
    }
    if (this.fixedCV) {
      this.fixedCV.classList.toggle('is-visible', visible);
      this.fixedCV.classList.toggle('is-hidden', !visible);
    }
  }
}
